import { TextField, Tooltip, Typography, Box, IconButton, Button } from '@mui/material';
import { ClassNames } from '@emotion/react';
import PropTypes from 'prop-types';
import {
  ComboBox,
  FreeSolo,
  MultiSelect,
  ComboBoxCreatableExpert,
  ComboBoxCreatableOutlet,
  FileTableWithDragAndDrop,
  SingleSelectLinkedService,
  ComboBoxCreatableDepartment,
  ComboBoxCreatableCommunityMember,
  ComboBoxAudience,
  ComboBoxCreatableJournalist,
  MultiSelectOrganizations,
} from '../Cells/CellTypes';
import {
  ContentLogKeys,
  DisplayKeys,
  EngagementKeys,
  IssueAndServiceSharedValues,
  ServiceKeys,
} from '../../shared/constants/IssueAndServiceKeys';
import { useSelector } from 'react-redux';
import { ContactMethodName, diversityOptions, TrackerDataType } from '../../shared/constants/constants';
import { useRef, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import AddLinkIcon from '@mui/icons-material/AddLink';
import RichTextEditor from '../RichTextEditor/RichTextEditor';
import DatePickerInputField from '../../shared/inputs/DatePickerInputField';
import StatusInputSelection from '../../shared/inputs/StatusInputSelection';
import ComplexityInputSelection from '../../shared/inputs/ComplexityInputSelection';
import ToneInputSelection from '../../shared/inputs/ToneInputSelection';
import { TYPE_TOOLTIP_PLACEHOLDER } from '../../shared/constants/tableTooltips';
import LiveLinkDialog from './LiveLinkDialog';
import UrlDialog from '../ServiceLog/UrlDialog';
import { queryAdderPalette, queryAdderTypeScale } from '../../shared/constants/queryAdderPalette';
import { semanticTypography } from '../../theme/typography';
import { calendarSnapshotChipTypography } from '../Calendar/CalendarSnapshotChip';
import { adderSectionIconColor, queryAdderTooltipProps, queryAdderTooltipTitleSx } from './queryAdderPopupStyles';

const UrlUploadButton = ({ setFormOpen, disabled = false }) => {
  return (
    <Tooltip
      title={<Typography sx={queryAdderTooltipTitleSx}>Add a web-hosted file link</Typography>}
      {...queryAdderTooltipProps}
    >
      <IconButton
        disabled={disabled}
        color="primary"
        onClick={() => setFormOpen(true)}
        size="large"
        data-cy="modal-url-upload-button"
      >
        <AddLinkIcon />
      </IconButton>
    </Tooltip>
  );
};

UrlUploadButton.propTypes = {
  setFormOpen: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const getLiveLinkActionButtonSx = ({ borderColor, color }) => ({
  width: 'fit-content',
  minWidth: 'unset',
  height: 28,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textTransform: 'none',
  borderRadius: '10px',
  borderColor,
  color,
  backgroundColor: queryAdderPalette.common.white,
  fontSize: '11px',
  fontWeight: 700,
  lineHeight: 1,
  px: 0.75,
  '&:hover': {
    borderColor,
    backgroundColor: queryAdderPalette.common.white,
  },
  '& .MuiButton-startIcon': {
    marginRight: 2,
    marginLeft: 0,
    display: 'inline-flex',
    alignItems: 'center',
  },
  '& .MuiButton-startIcon svg': {
    fontSize: 13,
  },
});

const queryAdderFieldLabelTypography = {
  ...semanticTypography.fieldLabel,
  fontSize: queryAdderTypeScale.fieldLabel,
};

const queryAdderModalChipFontSize = `calc(${calendarSnapshotChipTypography.fontSize} + 2pt)`;
const queryAdderEditorLongTextFontSize = `calc(${queryAdderTypeScale.helperText} + 1pt)`;

const queryAdderChipTypographySx = {
  minHeight: 34,
  height: 34,
  px: 1.5,
  borderRadius: '999px',
  fontFamily: calendarSnapshotChipTypography.fontFamily,
  fontSize: queryAdderModalChipFontSize,
  fontWeight: calendarSnapshotChipTypography.fontWeight,
  lineHeight: calendarSnapshotChipTypography.lineHeight,
  letterSpacing: calendarSnapshotChipTypography.letterSpacing,
  '& .MuiChip-label': {
    fontFamily: calendarSnapshotChipTypography.fontFamily,
    fontSize: queryAdderModalChipFontSize,
    fontWeight: calendarSnapshotChipTypography.fontWeight,
    lineHeight: calendarSnapshotChipTypography.lineHeight,
    letterSpacing: calendarSnapshotChipTypography.letterSpacing,
    px: 1.5,
  },
};

const CONTENT_TYPE_CATEGORY_VALUES = new Set(['content', 'content type']);

const normalizeTypeGroupName = (value) => (typeof value === 'string' ? value.trim().toLowerCase() : '');
const isContentTypeGroup = (value) => CONTENT_TYPE_CATEGORY_VALUES.has(normalizeTypeGroupName(value));

const sortTypeOptionsForQueryAdder = (types = [], prioritizeContentGroup = false) => {
  return [...types].sort((a, b) => {
    const groupA = a?.type ?? '';
    const groupB = b?.type ?? '';
    const aIsContentGroup = isContentTypeGroup(groupA);
    const bIsContentGroup = isContentTypeGroup(groupB);

    if (prioritizeContentGroup && aIsContentGroup !== bIsContentGroup) {
      return aIsContentGroup ? -1 : 1;
    }

    const groupOrder = String(groupA).localeCompare(String(groupB));
    if (groupOrder !== 0) {
      return groupOrder;
    }

    return String(a?.name ?? '').localeCompare(String(b?.name ?? ''));
  });
};

function getFileActionColors(type) {
  switch (type) {
    case TrackerDataType.ENGAGEMENT:
      return {
        borderColor: queryAdderPalette.engagement.uploadBorder,
        color: queryAdderPalette.engagement.accentDark,
        backgroundColor: queryAdderPalette.common.white,
      };
    case TrackerDataType.SERVICE:
      return {
        borderColor: queryAdderPalette.service.uploadBorder,
        color: queryAdderPalette.service.main,
        backgroundColor: queryAdderPalette.common.white,
      };
    case TrackerDataType.CONTENT_LOG:
      return {
        borderColor: queryAdderPalette.content.uploadBorder,
        color: queryAdderPalette.content.main,
        backgroundColor: queryAdderPalette.common.white,
      };
    case TrackerDataType.MEDIA_INTERACTION:
      return {
        borderColor: queryAdderPalette.mediaInteraction.uploadBorder,
        color: queryAdderPalette.mediaInteraction.main,
        backgroundColor: queryAdderPalette.common.white,
      };
    default:
      return null;
  }
}

function QueryAdderCell(props) {
  const {
    col,
    fields,
    dropdownTable,
    callback,
    className,
    required,
    error,
    eventHandler,
    files,
    handleDrag,
    handleDrop,
    isDragActive,
    isUpdating = false,
    isBulkUpdate = false,
    hideLabel = false,
    labelText,
    fullWidthContainer = false,
    placeholderText,
    autoFocusLinkedService = false,
    fileRenderMode = 'full',
    liveLinkRenderMode = 'full',
  } = props;

  let options = [];
  const currentUser = useSelector((state) => state.user.userInfo);

  const fileActionColors = getFileActionColors(eventHandler?.type);

  const dropdowns = dropdownTable || {};
  const users = dropdowns['lead'] || [];
  const journalists = dropdowns['journalist'] || [];
  const communityMembers = dropdowns['communityMember'] || [];
  const audiences = dropdowns['audience'] || [];
  const experts = dropdowns['expert'] || [];
  const contentTypes = dropdowns['contentType'] || [];

  const topicOptionsByType = {
    [TrackerDataType.MEDIA_INTERACTION]: dropdowns['topic'] || [],
    [TrackerDataType.ENGAGEMENT]: dropdowns['engagementTopic'] || [],
    [TrackerDataType.CONTENT_LOG]: dropdowns['contentLogTopic'] || [],
  };

  const topics = topicOptionsByType[eventHandler?.type] || dropdowns['topic'] || [];
  const orgAgents = dropdowns['orgAgents'] || {};
  const units = dropdowns['units'] || [];

  const preSelectUser = [
    {
      _id: currentUser.id,
      name: currentUser.name,
    },
  ];

  const [isUrlFormOpen, setIsUrlFormOpen] = useState(false);
  const [isLiveLinkDialogOpen, setIsLiveLinkDialogOpen] = useState(false);
  const [editingLiveLink, setEditingLiveLink] = useState(null);
  const file = useRef(null);

  const handleUrlDialogSubmit = (onChange, dialogValue, handleClose, colName) => async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const nextUrlEntry = {
      path: dialogValue.url,
      name: dialogValue.name,
      description: dialogValue.description,
      type: 'url',
    };

    onChange([...(files || []), nextUrlEntry], colName);
    handleClose();
  };

  const FileUploadButton = ({ onChange, colName }) => {
    const handleFileSubmit = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.target.files.length) {
        onChange([...files, ...e.target.files], colName);
      }
    };

    return (
      <>
        <input type="file" multiple id="file" ref={file} onChange={handleFileSubmit} style={{ display: 'none' }} />
        <Tooltip title={<Typography sx={queryAdderTooltipTitleSx}>Add files</Typography>} {...queryAdderTooltipProps}>
          <label htmlFor="file" data-cy="modal-file-upload-button">
            <IconButton color="primary" onClick={() => file.current.click()} size="large">
              <AddRoundedIcon />
            </IconButton>
          </label>
        </Tooltip>
      </>
    );
  };

  return (
    <ClassNames>
      {({ css }) => {
        const classes = {
          mediumCell: css({
            display: 'flex',
            flexDirection: 'column',
            width: fullWidthContainer ? '100%' : '49%',
          }),
          smallCell: css({
            display: 'flex',
            flexDirection: 'column',
            width: fullWidthContainer ? '100%' : '23.5%',
          }),
          bigCell: css({
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }),
          datePicker: css({
            height: '56px',
            width: '100%',
          }),
          datePickerInput: css({
            height: '56px',
            width: '100%',
          }),
          commsCell: css({
            flex: '1 0 0',
            minWidth: '100%',
            display: 'block',
            fontWeight: 400,
          }),
          dateLabel: css({
            flex: '0 0 auto',
            maxWidth: '100%',
            minWidth: 0,
            marginBottom: '10px',
            ...queryAdderFieldLabelTypography,
          }),
          label: css({
            flex: '0 0 auto',
            maxWidth: '100%',
            minWidth: 0,
            marginBottom: '10px',
            ...queryAdderFieldLabelTypography,
          }),
          labelError: css({
            color: '#d32f2f',
          }),
          topicLabel: css({
            fontWeight: 'inherit',
            fontSize: 'inherit',
          }),
        };

        const renderInputLabel = (className, nextLabelText, required, error) => {
          if (hideLabel) {
            return null;
          }
          return (
            <label htmlFor={nextLabelText} className={`${className} ${error ? classes.labelError : ''}`}>
              {`${nextLabelText} ${required ? '*' : ''}`}
            </label>
          );
        };

        const resolvedLabelText = labelText || DisplayKeys[col] || col;

        let cell = (
          <label className={classes.label} htmlFor={resolvedLabelText}>
            <TextField
              align="left"
              className={`${className || ''}`}
              InputLabelProps={{
                className: `${col === 'service' ? classes.topicLabel : ''}`,
              }}
              value={fields[col] ? fields[col] : ''}
              label={resolvedLabelText}
              onChange={(e) => {
                callback(e.target.value, col);
              }}
              required={required}
              error={error}
              variant="standard"
              data-cy={`${col}-input-QueryAdderCell`}
            />
          </label>
        );

        switch (col) {
          case 'date':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.dateLabel, resolvedLabelText, required, error)}
                <DatePickerInputField
                  classes={{
                    textInputFieldContainerClass: classes.datePicker,
                    textInputFieldClass: classes.datePickerInput,
                  }}
                  calendarIconColor={adderSectionIconColor}
                  selected={fields[col] || new Date()}
                  onChange={(date) => {
                    callback(date, col);
                  }}
                  required={required}
                  data-cy="datePicker-QueryAdderCell"
                />
              </Box>
            );
            break;

          case 'entryDate': {
            let selected = fields[col];

            if (!isBulkUpdate && !selected) {
              selected = new Date();
            }

            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.dateLabel, resolvedLabelText, required, error)}
                <DatePickerInputField
                  classes={{
                    textInputFieldContainerClass: classes.datePicker,
                    textInputFieldClass: classes.datePickerInput,
                  }}
                  calendarIconColor={adderSectionIconColor}
                  selected={selected}
                  onChange={(date) => {
                    callback(date, col);
                  }}
                  onClear={() => callback(null, col)}
                  required={required}
                  data-cy="datePicker-QueryAdderCell-entryDate"
                />
              </Box>
            );
            break;
          }

          case 'publishDate':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.dateLabel, resolvedLabelText, required, error)}
                <DatePickerInputField
                  classes={{
                    textInputFieldContainerClass: classes.datePicker,
                    textInputFieldClass: classes.datePickerInput,
                  }}
                  calendarIconColor={adderSectionIconColor}
                  selected={fields[col]}
                  onChange={(date) => {
                    callback(date, col);
                  }}
                  required={required}
                  data-cy="datePicker-QueryAdderCell-publishDate"
                />
              </Box>
            );
            break;

          case 'dueDate':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.dateLabel, resolvedLabelText, required, error)}
                <DatePickerInputField
                  classes={{
                    textInputFieldContainerClass: classes.datePicker,
                    textInputFieldClass: classes.datePickerInput,
                  }}
                  calendarIconColor={adderSectionIconColor}
                  selected={fields[col]}
                  onChange={(date) => {
                    callback(date, col);
                  }}
                  onClear={() => callback(null, col)}
                  required={required}
                  data-cy="datePicker-QueryAdderCell-dueDate"
                />
              </Box>
            );
            break;

          case 'service':
            cell = (
              <Box className={classes.bigCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <TextField
                  align="left"
                  className={`${className || ''}`}
                  InputLabelProps={{
                    className: `${col === 'service' ? classes.topicLabel : ''}`,
                  }}
                  value={fields[col] ? fields[col] : ''}
                  onChange={(e) => {
                    callback(e.target.value, col);
                  }}
                  required={required}
                  error={error}
                  variant="outlined"
                  data-cy="service-input-QueryAdderCell"
                />
              </Box>
            );
            break;

          case 'topic':
            cell = (
              <Box className={classes.bigCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <FreeSolo
                  list={topics}
                  callback={callback}
                  col={col}
                  className={`${className || ''} ${classes.topicLabel}`}
                  required={required}
                  value={fields[col]}
                  error={error}
                  textFieldVariant="outlined"
                  displayLabel={false}
                  placeholder={placeholderText}
                />
              </Box>
            );
            break;

          case 'otherNotes':
            cell = (
              <Box className={classes.bigCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <FreeSolo
                  list={dropdowns[col] || []}
                  callback={callback}
                  col={col}
                  className={className}
                  required={required}
                  value={fields[col]}
                  error={error}
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;

          case 'tone':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <ToneInputSelection
                  selectedTones={fields[col] ? [fields[col]] : []}
                  onSelect={(selectedTones) => callback(selectedTones.length > 0 ? selectedTones[0] : undefined, col)}
                  chipSx={queryAdderChipTypographySx}
                />
              </Box>
            );
            break;

          case 'lead':
            if (!fields[col] && !isBulkUpdate) {
              fields[col] = preSelectUser;
            }

            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <MultiSelect
                  options={users}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  data-cy="lead-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;

          case IssueAndServiceSharedValues.leadExpert:
          case 'Expert Required':
          case 'unitLead':
          case 'expert':
            options = fields[col];
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <ComboBoxCreatableExpert
                  units={units}
                  options={experts.filter((expert) => !expert.archived)}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  setVal={(v) => (fields[col] = v)}
                  className={className}
                  required={required}
                  error={error}
                  popupType={eventHandler?.type}
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;

          case 'teamMember': {
            if (!fields[col] && !isBulkUpdate) {
              fields[col] = preSelectUser;
            }

            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <MultiSelect
                  options={users}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  data-cy="teamMember-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          }

          case 'outlet':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <ComboBoxCreatableOutlet
                  options={(dropdowns['outlet'] || []).filter((outlet) => !outlet.archived)}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  setVal={(v) => (fields[col] = v)}
                  className={className}
                  required={required}
                  error={error}
                  popupType={eventHandler?.type}
                  data-cy="outlet-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;

          case 'organization':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <MultiSelectOrganizations
                  options={(dropdowns['organization'] || []).filter((organization) => !organization.archived)}
                  callback={callback}
                  val={Array.isArray(fields[col]) ? fields[col] : []}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  popupType={eventHandler?.type}
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;

          case 'journalist':
            options = fields['outlet']
              ? orgAgents[fields['outlet']]?.filter((journalist) => !journalist.archived)
              : journalists.filter((journalist) => !journalist.archived);

            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <ComboBoxCreatableJournalist
                  orgs={dropdowns['outlet'] || []}
                  options={options}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  selectedOrg={fields['outlet']}
                  popupType={eventHandler?.type}
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;

          case 'communityMember': {
            let selectedOrganizationIds = [];

            if (Array.isArray(fields['organization'])) {
              selectedOrganizationIds = fields['organization']
                .map((organization) => organization?._id ?? organization)
                .filter(Boolean);
            }

            let scopedOptions;

            if (selectedOrganizationIds.length > 0) {
              scopedOptions = communityMembers
                .filter((communityMember) => !communityMember.archived)
                .filter((communityMember) => {
                  const organizationRef = communityMember?.Organization;

                  let organizationId = null;

                  if (typeof organizationRef === 'string') {
                    organizationId = organizationRef;
                  } else if (organizationRef?._id) {
                    organizationId = organizationRef._id;
                  } else if (organizationRef?.id) {
                    organizationId = organizationRef.id;
                  }

                  if (!organizationId) {
                    return false;
                  }

                  return selectedOrganizationIds.includes(organizationId);
                });
            } else {
              scopedOptions = communityMembers.filter((communityMember) => !communityMember.archived);
            }

            const currentCommunityMembers = Array.isArray(fields[col]) ? fields[col] : [];

            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <ComboBoxCreatableCommunityMember
                  orgs={dropdowns['organization'] || []}
                  options={scopedOptions}
                  callback={callback}
                  val={currentCommunityMembers}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  selectedOrg={fields['organization']}
                  popupType={eventHandler?.type}
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          }

          case 'audience': {
            const currentAudiences = Array.isArray(fields[col]) ? fields[col] : [];

            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <ComboBoxAudience
                  options={audiences.filter((audience) => !audience.archived)}
                  callback={callback}
                  val={currentAudiences}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;
          }

          case 'dept':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <ComboBoxCreatableDepartment
                  options={(dropdowns['dept'] || []).filter((dept) => !dept.archived)}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  popupType={eventHandler?.type}
                  data-cy="dept-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;

          case 'campaign':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <MultiSelect
                  options={(dropdowns['campaigns'] || []).filter((campaign) => !campaign.archived)}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  groupBy={(option) => option.campaigns}
                  renderOptionWithTooltip
                  disableTooltip
                  required={required}
                  error={error}
                  data-cy="campaign-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;

          case 'units':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <MultiSelect
                  options={(dropdowns['units'] || []).filter((unit) => !unit.archived).map((unit) => unit.name)}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  required={required}
                  error={error}
                  groupBy={(option) => option.units}
                  data-cy="units-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;

          case 'type':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <MultiSelect
                  options={sortTypeOptionsForQueryAdder(
                    (dropdowns['type'] || []).filter(
                      (typeOption) =>
                        !typeOption.archived &&
                        !(eventHandler?.type === TrackerDataType.CONTENT_LOG && isContentTypeGroup(typeOption.type))
                    ),
                    false
                  )}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  groupBy={(option) => option.type}
                  renderOptionWithTooltip
                  tooltipPlaceholder={TYPE_TOOLTIP_PLACEHOLDER}
                  disableTooltip
                  required={required}
                  error={error}
                  data-cy="type-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;

          case 'contentType':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <ComboBox
                  options={contentTypes.filter(
                    (contentType) =>
                      [fields[col]].some(
                        (selectedContentType) =>
                          (selectedContentType?._id ?? selectedContentType?.name ?? selectedContentType) ===
                          (contentType._id ?? contentType.name)
                      ) || contentType.archived !== true
                  )}
                  callback={callback}
                  val={fields[col]?._id ?? fields[col]?.name ?? fields[col] ?? null}
                  col={col}
                  required={required}
                  error={error}
                  renderSingleSelectColorChip
                  data-cy="contentType-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;

          case 'linkedService':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <SingleSelectLinkedService
                  options={dropdowns['service'] || []}
                  callback={callback}
                  val={fields[col]?.length > 0 ? fields[col][0] : ''}
                  col={col}
                  required={required}
                  error={error}
                  placeholder={placeholderText}
                  openOnFocus
                  autoFocus={autoFocusLinkedService}
                  ListboxProps={{ sx: { maxHeight: 320 } }}
                  data-cy="linkedService-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;

          case 'complexity':
            cell = (
              <Box className={classes.smallCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <ComplexityInputSelection
                  selectedComplexities={fields[col] ? [fields[col]] : []}
                  onSelect={(selectedComplexities) =>
                    callback(selectedComplexities.length > 0 ? selectedComplexities[0] : undefined, col)
                  }
                />
              </Box>
            );
            break;

          case 'diversity':
            cell = (
              <Box className={classes.smallCell}>
                {renderInputLabel(classes.label, DisplayKeys[col] || col, required, error)}
                <ComboBox
                  options={diversityOptions}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  required={required}
                  error={error}
                  data-cy="diversity-comboBox-QueryAdderCell"
                  isConstant
                  textFieldVariant="outlined"
                  displayLabel={false}
                />
              </Box>
            );
            break;

          case 'contactMethod': {
            const contactMethodLabelMap = {
              [ContactMethodName.NO_INTERMEDIARY]: 'in person',
              [ContactMethodName.PHONE]: 'phone call',
            };

            const getContactMethodLabel = (option) => {
              const name = option?.name ?? '';
              return contactMethodLabelMap[name] || name;
            };

            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, labelText || 'Mode of Communication', required, error)}
                <ComboBox
                  options={dropdowns['contactMethod'] || []}
                  callback={callback}
                  val={fields[col]}
                  col={col}
                  className={className}
                  required={required}
                  error={error}
                  data-cy="contactMethod-select-QueryAdderCell"
                  textFieldVariant="outlined"
                  displayLabel={false}
                  getOptionLabel={getContactMethodLabel}
                  disablePortal={false}
                />
              </Box>
            );
            break;
          }

          case 'linkedInteraction':
          case 'linkedEngagement':
            cell = <></>;
            break;

          case 'file': {
            let fileLabel = ContentLogKeys.file;
            const showUploadControls = fileRenderMode !== 'listOnly';
            const showFileList = fileRenderMode !== 'uploadOnly';

            if (eventHandler?.type === TrackerDataType.SERVICE) {
              fileLabel = ServiceKeys.file;
            } else if (eventHandler?.type === TrackerDataType.ENGAGEMENT) {
              fileLabel = EngagementKeys.file;
            }

            const isMediaStoryLinkType = eventHandler?.type === TrackerDataType.MEDIA_INTERACTION;
            const isFileUploadActionType =
              eventHandler?.type === TrackerDataType.ENGAGEMENT ||
              eventHandler?.type === TrackerDataType.CONTENT_LOG ||
              eventHandler?.type === TrackerDataType.SERVICE ||
              eventHandler?.type === TrackerDataType.MEDIA_INTERACTION;
            const sharedUploadActionButtonSx = {
              minWidth: 104,
              height: 30,
              textTransform: 'none',
              borderRadius: '8px',
              ...fileActionColors,
              px: 1.1,
              fontSize: '11px',
              fontWeight: 700,
              '& .MuiButton-startIcon': {
                marginRight: 2,
                marginLeft: 0,
                display: 'inline-flex',
                alignItems: 'center',
              },
              '& .MuiButton-startIcon svg': {
                fontSize: 14,
              },
            };
            const uploadActionControls = isFileUploadActionType ? (
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                {isMediaStoryLinkType ? null : (
                  <>
                    <input
                      type="file"
                      multiple
                      id="file"
                      ref={file}
                      onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        if (e.target.files.length) {
                          callback([...files, ...e.target.files], col);
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="file" data-cy="modal-file-upload-button" style={{ display: 'flex' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddRoundedIcon />}
                        onClick={() => file.current?.click()}
                        sx={sharedUploadActionButtonSx}
                      >
                        Add File
                      </Button>
                    </label>
                  </>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddLinkIcon />}
                  onClick={() => setIsUrlFormOpen(true)}
                  sx={sharedUploadActionButtonSx}
                >
                  Add Link
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <FileUploadButton file={file} onChange={callback} colName={col} />
                <UrlUploadButton setFormOpen={setIsUrlFormOpen} />
              </Box>
            );

            const uploadOnlyStoryLink =
              hideLabel && fileRenderMode === 'uploadOnly' && eventHandler?.type === TrackerDataType.MEDIA_INTERACTION;

            cell = (
              <Box style={{ minHeight: uploadOnlyStoryLink ? 0 : '5vh' }} className={classes.commsCell}>
                {showUploadControls ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: hideLabel ? 'center' : 'space-between',
                      alignItems: 'flex-start',
                      gap: 2,
                      mb: showFileList && files?.length > 0 ? 1.5 : 0,
                    }}
                  >
                    {hideLabel ? null : <Typography sx={queryAdderFieldLabelTypography}>{fileLabel}</Typography>}
                    {uploadActionControls}
                  </Box>
                ) : null}
                {showFileList && files?.length > 0 ? (
                  <Box sx={{ width: '100%' }}>
                    <FileTableWithDragAndDrop
                      col={col}
                      files={files}
                      onDelete={callback}
                      handleDrag={showUploadControls ? handleDrag : undefined}
                      handleDrop={showUploadControls ? handleDrop : undefined}
                      isDragActive={isDragActive}
                      isUpdating={isUpdating}
                      allowStagedDelete={!isUpdating}
                      popupType={eventHandler?.type}
                    />
                  </Box>
                ) : null}
                <UrlDialog
                  isOpen={isUrlFormOpen}
                  setIsOpen={setIsUrlFormOpen}
                  callback={callback}
                  type={eventHandler?.type}
                  colName={col}
                  handleSubmit={handleUrlDialogSubmit}
                />
              </Box>
            );
            break;
          }

          case 'liveLink': {
            const currentLink = fields[col] ? [{ ...fields[col], type: 'url' }] : [];
            const liveLinkValue = currentLink[0] || null;
            const liveLinkActionLabel = liveLinkValue ? 'Edit Live Link' : 'Add Live Link';
            const showLiveLinkAction = liveLinkRenderMode !== 'listOnly';
            const showLiveLinkList = liveLinkRenderMode !== 'actionOnly' && Boolean(liveLinkValue);
            const shouldRenderLiveLinkActionButton =
              showLiveLinkAction && !(liveLinkValue && liveLinkRenderMode === 'actionOnly');
            let liveLinkActionJustifyContent = 'space-between';
            if (hideLabel) {
              liveLinkActionJustifyContent = 'flex-end';
            }
            if (hideLabel && liveLinkRenderMode === 'actionOnly') {
              liveLinkActionJustifyContent = 'center';
            }

            if (eventHandler?.type === TrackerDataType.CONTENT_LOG) {
              cell = (
                <Box sx={{ width: '100%' }}>
                  {shouldRenderLiveLinkActionButton ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: liveLinkActionJustifyContent,
                        alignItems: 'flex-start',
                        gap: 2,
                        mb: showLiveLinkList ? 1.5 : 0,
                      }}
                    >
                      {!hideLabel && (
                        <Box>
                          <Typography sx={queryAdderFieldLabelTypography}>{ContentLogKeys.liveLink}</Typography>
                          <Typography
                            sx={{
                              mt: 0.5,
                              fontSize: '12px',
                              lineHeight: 1.6,
                              color: queryAdderPalette.common.helper,
                            }}
                          >
                            Add the published story URL with a clear title and short description once coverage goes
                            live.
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddLinkIcon />}
                          onClick={() => {
                            setEditingLiveLink(liveLinkValue);
                            setIsLiveLinkDialogOpen(true);
                          }}
                          sx={getLiveLinkActionButtonSx({
                            borderColor: queryAdderPalette.content.main,
                            color: queryAdderPalette.content.main,
                          })}
                        >
                          {liveLinkActionLabel}
                        </Button>
                      </Box>
                    </Box>
                  ) : null}

                  {showLiveLinkList ? (
                    <FileTableWithDragAndDrop
                      col={col}
                      files={currentLink}
                      onDelete={() => callback(null, col)}
                      isUpdating={isUpdating}
                      allowStagedDelete
                      popupType={eventHandler?.type}
                      onEditItem={(fileToEdit) => {
                        setEditingLiveLink(fileToEdit);
                        setIsLiveLinkDialogOpen(true);
                      }}
                    />
                  ) : null}

                  <LiveLinkDialog
                    open={isLiveLinkDialogOpen}
                    setOpen={setIsLiveLinkDialogOpen}
                    initialValue={editingLiveLink}
                    onSubmit={(value) => callback(value, col)}
                    onDelete={() => callback(null, col)}
                    type={eventHandler?.type}
                  />
                </Box>
              );
              break;
            }

            cell = (
              <Box style={{ minHeight: '5vh' }} className={classes.commsCell}>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Typography>{ContentLogKeys.liveLink}</Typography>
                  <UrlUploadButton
                    disabled={currentLink.length > 0}
                    setFormOpen={() => {
                      setEditingLiveLink(null);
                      setIsLiveLinkDialogOpen(true);
                    }}
                  />
                </Box>
                <FileTableWithDragAndDrop
                  col={col}
                  files={currentLink}
                  onDelete={() => callback(null, col)}
                  isUpdating={isUpdating}
                  allowStagedDelete
                  popupType={eventHandler?.type}
                  onEditItem={(fileToEdit) => {
                    setEditingLiveLink(fileToEdit);
                    setIsLiveLinkDialogOpen(true);
                  }}
                />
                <LiveLinkDialog
                  open={isLiveLinkDialogOpen}
                  setOpen={setIsLiveLinkDialogOpen}
                  initialValue={editingLiveLink}
                  onSubmit={(value) => callback(value, col)}
                  onDelete={() => callback(null, col)}
                  type={eventHandler?.type}
                />
              </Box>
            );
            break;
          }

          case 'actions':
          case 'response':
          case 'background':
          case 'content':
          case 'notes':
          case 'keyMessaging':
            cell = (
              <Box className={classes.bigCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <RichTextEditor
                  defaultValue={''}
                  value={fields[col]}
                  onChange={(value) => {
                    callback(value, col);
                  }}
                  required={required}
                  error={error}
                  dataCy={`${col}-input-QueryAdderCell`}
                  editorFontSize={isUpdating ? queryAdderEditorLongTextFontSize : undefined}
                  readOnly={false}
                />
              </Box>
            );
            break;

          case 'status':
            cell = (
              <Box className={classes.mediumCell}>
                {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                <StatusInputSelection
                  options={(dropdowns[col] || []).map((item) => item.name)}
                  selectedStatus={fields[col] ? [fields[col]] : []}
                  onSelect={(selectedStatus) => {
                    callback(selectedStatus.length > 0 ? selectedStatus[0] : undefined, col);
                  }}
                  includeInApplicationServiceOrIssueStatus={false}
                  chipSx={queryAdderChipTypographySx}
                />
              </Box>
            );
            break;

          default:
            if (col !== 'service' && Object.prototype.hasOwnProperty.call(dropdowns, col)) {
              cell = (
                <Box className={classes.mediumCell}>
                  {renderInputLabel(classes.label, resolvedLabelText, required, error)}
                  <ComboBox
                    options={dropdowns[col]}
                    callback={callback}
                    val={fields[col]}
                    col={col}
                    className={className}
                    required={required}
                    error={error}
                    data-cy={`${col}-select-QueryAdderCell`}
                    textFieldVariant="outlined"
                    displayLabel={false}
                  />
                </Box>
              );
            }
            break;
        }

        return <>{cell}</>;
      }}
    </ClassNames>
  );
}

QueryAdderCell.propTypes = {
  col: PropTypes.string.isRequired,
  fields: PropTypes.object.isRequired,
  dropdownTable: PropTypes.shape({
    lead: PropTypes.array,
    journalist: PropTypes.array,
    communityMember: PropTypes.array,
    audience: PropTypes.array,
    expert: PropTypes.array,
    topic: PropTypes.array,
    engagementTopic: PropTypes.array,
    contentLogTopic: PropTypes.array,
    orgAgents: PropTypes.object,
    units: PropTypes.array,
    outlet: PropTypes.array,
    organization: PropTypes.array,
    dept: PropTypes.array,
    campaigns: PropTypes.array,
    contentType: PropTypes.array,
    type: PropTypes.array,
    service: PropTypes.array,
    contactMethod: PropTypes.array,
    status: PropTypes.array,
  }),
  callback: PropTypes.func.isRequired,
  className: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.bool,
  eventHandler: PropTypes.shape({
    type: PropTypes.oneOf([
      TrackerDataType.MEDIA_INTERACTION,
      TrackerDataType.SERVICE,
      TrackerDataType.ENGAGEMENT,
      TrackerDataType.CONTENT_LOG,
    ]),
  }),
  files: PropTypes.array,
  handleDrag: PropTypes.func,
  handleDrop: PropTypes.func,
  isDragActive: PropTypes.bool,
  isUpdating: PropTypes.bool,
  isBulkUpdate: PropTypes.bool,
  hideLabel: PropTypes.bool,
  labelText: PropTypes.string,
  fullWidthContainer: PropTypes.bool,
  placeholderText: PropTypes.string,
  autoFocusLinkedService: PropTypes.bool,
  fileRenderMode: PropTypes.oneOf(['full', 'uploadOnly', 'listOnly']),
  liveLinkRenderMode: PropTypes.oneOf(['full', 'actionOnly', 'listOnly']),
};

export { QueryAdderCell };
